import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import SharedHeader from "@/components/SharedHeader";
import SharedFooter from "@/components/SharedFooter";
import SEO, { serviceSchema, faqSchema, breadcrumbSchema } from "@/components/SEO";
import {
  Lightbulb,
  CheckCircle,
  ArrowRight,
  Camera,
  MessageSquare,
  Brain,
  Clock,
  Shield,
  Star,
  Zap,
  Gift,
  Sparkles,
  Globe,
} from "lucide-react";

// 11 supported languages
const SUPPORTED_LANGUAGES = [
  "English", "Hindi", "Tamil", "Telugu", "Kannada", 
  "Marathi", "Punjabi", "Bengali", "Gujarati", "Malayalam", "Odia"
];

// Credit packages for homework help
const HOMEWORK_PACKAGES = [
  {
    id: "free",
    name: "Free Trial",
    price: 0,
    credits: 15,
    validity: "30 days",
    icon: Gift,
    color: "purple",
    badge: "FREE",
    perHelp: "₹0",
  },
  {
    id: "starter",
    name: "Starter",
    price: 399,
    credits: 100,
    validity: "30 days",
    icon: Zap,
    color: "emerald",
    perHelp: "₹3.99",
  },
  {
    id: "popular",
    name: "Popular",
    price: 799,
    credits: 250,
    validity: "30 days",
    icon: Star,
    color: "amber",
    popular: true,
    perHelp: "₹3.20",
    discount: "20%",
  },
  {
    id: "value",
    name: "Value Pack",
    price: 1399,
    credits: 500,
    validity: "30 days",
    icon: Sparkles,
    color: "indigo",
    perHelp: "₹2.80",
    discount: "30%",
  },
];

export default function EduAssist() {
  const navigate = useNavigate();

  // Structured data for EduAssist service
  const eduAssistStructuredData = {
    "@context": "https://schema.org",
    "@graph": [
      serviceSchema({
        name: "EduAssist - AI Homework Helper",
        description:
          "Instant AI-powered homework help with step-by-step guidance using the Socratic method. Photo upload with OCR, unlimited follow-up questions, available 24/7 in 11 Indian languages.",
        price: "0",
      }),
      breadcrumbSchema([
        { name: "Home", url: "/" },
        { name: "EduAssist Homework Helper", url: "/eduassist" },
      ]),
      faqSchema([
        {
          question: "How does EduAssist help with homework?",
          answer:
            "EduAssist uses AI to guide you step-by-step through homework problems using the Socratic method. Instead of giving direct answers, it asks guiding questions to help you understand and solve problems yourself.",
        },
        {
          question: "Can I upload photos of my homework?",
          answer:
            "Yes! EduAssist has OCR technology that can read both handwritten and printed text from photos. Simply snap a picture of your homework question.",
        },
        {
          question: "How much does EduAssist cost?",
          answer:
            "EduAssist offers 15 free homework helps to start. After that, packages range from ₹399 for 100 helps to ₹1,399 for 500 helps.",
        },
      ]),
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-b from-gray-50 to-white">
      <SEO
        title="EduAssist - AI Homework Helper | Step-by-Step Guidance | Eduzy"
        description="Get instant AI homework help with EduAssist. Upload photos, get step-by-step guidance using the Socratic method. 15 free helps, then from ₹3/help. Available 24/7 in 11 Indian languages."
        keywords="homework help, AI tutor, homework solver, math help, science homework, CBSE homework, step by step solutions, Socratic method, OCR homework"
        canonicalUrl="/eduassist"
        structuredData={eduAssistStructuredData}
      />

      {/* Header */}
      <SharedHeader showNav={true} />

      {/* Hero Section */}
      <section className="relative py-12 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-500 via-violet-600 to-indigo-700 overflow-hidden">
        {/* Background decorative elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -right-40 w-96 h-96 bg-pink-400/20 rounded-full blur-3xl"></div>
          <div className="absolute top-1/3 -left-20 w-72 h-72 bg-cyan-400/20 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-1/4 w-56 h-56 bg-yellow-400/15 rounded-full blur-2xl"></div>
        </div>

        <div className="relative max-w-5xl mx-auto text-center">
          <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm text-white rounded-full px-4 py-1.5 mb-4 border border-white/30">
            <Lightbulb className="w-4 h-4" />
            <span className="font-medium text-sm">AI-Powered Homework Help</span>
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
            Stuck on Homework?
            <br />
            <span className="text-yellow-300">Get Instant AI Help</span>
          </h1>
          <p className="text-lg sm:text-xl text-white/90 max-w-2xl mx-auto mb-6">
            Upload a photo or type your question. Our AI tutor guides you step-by-step
            with hints and explanations — teaching you to solve problems, not just giving
            answers.
          </p>

          <div className="flex flex-col items-center justify-center gap-3">
            <Button
              onClick={() => navigate("/login?register=true&redirect=homework")}
              size="lg"
              className="bg-white text-violet-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-full font-semibold shadow-xl"
              data-testid="eduassist-hero-cta"
            >
              Try 15 Free Helps
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
            <p className="text-sm text-white/80">No credit card required</p>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8">
            How EduAssist Works
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-violet-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Camera className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">1. Upload or Type</h3>
              <p className="text-gray-600">
                Take a photo of your homework question or type it directly. Our OCR
                technology reads handwritten and printed text.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-violet-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">2. AI Guides You</h3>
              <p className="text-gray-600">
                Instead of direct answers, our AI uses the Socratic method — asking
                guiding questions and providing hints to help you think.
              </p>
            </div>
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-violet-500 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
                <MessageSquare className="w-8 h-8 text-white" />
              </div>
              <h3 className="font-bold text-gray-900 text-lg mb-2">3. Learn & Ask More</h3>
              <p className="text-gray-600">
                Understand the concept deeply. Ask unlimited follow-up questions for free
                until you truly get it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Languages Section */}
      <section className="py-8 px-4 sm:px-6 lg:px-8 bg-purple-50">
        <div className="max-w-5xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-3">
            <Globe className="w-6 h-6 text-purple-600" />
            <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
              Get Help in Your Language
            </h2>
          </div>
          <p className="text-gray-600 mb-6">Available in 11 Indian languages for better understanding</p>
          <div className="flex flex-wrap justify-center gap-2">
            {SUPPORTED_LANGUAGES.map((lang) => (
              <Badge 
                key={lang} 
                variant="secondary" 
                className="bg-white text-gray-700 border border-purple-200 px-3 py-1"
              >
                {lang}
              </Badge>
            ))}
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 text-center mb-8">
            Why Students Love EduAssist
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: Camera,
                title: "Photo Upload with OCR",
                desc: "Snap a photo of handwritten or printed homework. Our AI reads it instantly.",
                bgColor: "bg-gradient-to-br from-blue-50 to-cyan-50",
                borderColor: "border-blue-200",
                iconBg: "bg-blue-100",
                iconColor: "text-blue-600",
              },
              {
                icon: Brain,
                title: "Socratic Teaching Method",
                desc: "We guide with questions, not answers. Build real problem-solving skills.",
                bgColor: "bg-gradient-to-br from-purple-50 to-pink-50",
                borderColor: "border-purple-200",
                iconBg: "bg-purple-100",
                iconColor: "text-purple-600",
              },
              {
                icon: MessageSquare,
                title: "Unlimited Follow-ups",
                desc: "Ask as many clarifying questions as you need — they are completely free.",
                bgColor: "bg-gradient-to-br from-emerald-50 to-teal-50",
                borderColor: "border-emerald-200",
                iconBg: "bg-emerald-100",
                iconColor: "text-emerald-600",
              },
              {
                icon: Clock,
                title: "Available 24/7",
                desc: "Get help anytime — late night study sessions or early morning revisions.",
                bgColor: "bg-gradient-to-br from-amber-50 to-orange-50",
                borderColor: "border-amber-200",
                iconBg: "bg-amber-100",
                iconColor: "text-amber-600",
              },
              {
                icon: Shield,
                title: "All Subjects Covered",
                desc: "Math, Science, English, Hindi — help across the curriculum.",
                bgColor: "bg-gradient-to-br from-indigo-50 to-violet-50",
                borderColor: "border-indigo-200",
                iconBg: "bg-indigo-100",
                iconColor: "text-indigo-600",
              },
              {
                icon: Sparkles,
                title: "WhatsApp Integration",
                desc: "Get homework help directly on WhatsApp — no app download needed.",
                bgColor: "bg-gradient-to-br from-rose-50 to-pink-50",
                borderColor: "border-rose-200",
                iconBg: "bg-rose-100",
                iconColor: "text-rose-600",
              },
            ].map((feature) => (
              <div
                key={feature.title}
                className={`${feature.bgColor} rounded-xl p-6 border ${feature.borderColor} hover:shadow-lg transition-shadow`}
              >
                <div className={`w-12 h-12 ${feature.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-6 h-6 ${feature.iconColor}`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2">{feature.title}</h3>
                <p className="text-sm text-gray-600">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <Badge className="bg-purple-100 text-purple-700 border-0 mb-4">
              <Gift className="w-3 h-3 mr-1" />
              15 Free Helps to Start
            </Badge>
            <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">
              Simple, Affordable Pricing
            </h2>
            <p className="text-gray-600">
              Pay only for what you use. No subscriptions required.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {HOMEWORK_PACKAGES.map((pkg) => {
              const Icon = pkg.icon;
              const colorClasses = {
                purple: {
                  iconBg: "from-purple-400 to-violet-500",
                  check: "text-purple-600",
                  btn: "bg-purple-600 hover:bg-purple-700 text-white",
                  border: "border-purple-300",
                  badge: "bg-purple-600",
                  cardBg: "bg-purple-50",
                },
                emerald: {
                  iconBg: "from-emerald-400 to-teal-500",
                  check: "text-emerald-600",
                  btn: "border-emerald-600 text-emerald-600 hover:bg-emerald-50",
                  border: "border-gray-200 hover:border-emerald-300",
                  cardBg: "bg-white",
                },
                amber: {
                  iconBg: "from-amber-400 to-orange-500",
                  check: "text-amber-600",
                  btn: "bg-amber-500 hover:bg-amber-600 text-white",
                  border: "border-amber-300 shadow-lg shadow-amber-100",
                  badge: "bg-amber-500",
                  cardBg: "bg-amber-50",
                },
                indigo: {
                  iconBg: "from-indigo-400 to-purple-500",
                  check: "text-indigo-600",
                  btn: "border-indigo-600 text-indigo-600 hover:bg-indigo-50",
                  border: "border-gray-200 hover:border-indigo-300",
                  cardBg: "bg-white",
                },
              };
              const colors = colorClasses[pkg.color];

              return (
                <Card
                  key={pkg.id}
                  className={`relative overflow-hidden border-2 transition-all duration-300 hover:shadow-lg ${
                    pkg.popular ? colors.border + " scale-105" : colors.border
                  } ${colors.cardBg}`}
                  data-testid={`eduassist-pkg-${pkg.id}`}
                >
                  {pkg.popular && (
                    <div className="absolute top-0 left-0 right-0 bg-amber-500 text-white text-center py-1 text-xs font-bold">
                      BEST VALUE
                    </div>
                  )}

                  <CardContent className={`p-6 ${pkg.popular ? "pt-8" : ""}`}>
                    <div className="flex items-center gap-2 mb-3">
                      <div
                        className={`w-10 h-10 bg-gradient-to-br ${colors.iconBg} rounded-lg flex items-center justify-center`}
                      >
                        <Icon className="w-5 h-5 text-white" />
                      </div>
                      {pkg.badge && (
                        <span
                          className={`${colors.badge} text-white text-xs font-bold px-2 py-0.5 rounded-full`}
                        >
                          {pkg.badge}
                        </span>
                      )}
                    </div>

                    <h3 className="font-bold text-gray-900 text-xl">{pkg.name}</h3>

                    <div className="flex items-baseline gap-1 my-3">
                      {pkg.price === 0 ? (
                        <span className="text-3xl font-bold text-purple-600">₹0</span>
                      ) : (
                        <>
                          <span className="text-3xl font-bold text-gray-900">
                            ₹{pkg.price}
                          </span>
                          <span className="text-sm text-gray-500">
                            /{pkg.credits} helps
                          </span>
                        </>
                      )}
                    </div>

                    <ul className="space-y-2 text-sm text-gray-600 mb-6">
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${colors.check}`} />
                        <span>
                          <b>{pkg.credits}</b> homework helps
                        </span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${colors.check}`} />
                        <span>
                          {pkg.perHelp} per help{" "}
                          {pkg.discount && (
                            <span className="text-green-600 font-medium">
                              ({pkg.discount} off)
                            </span>
                          )}
                        </span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${colors.check}`} />
                        <span>{pkg.validity} validity</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <CheckCircle className={`w-4 h-4 ${colors.check}`} />
                        <span>Unlimited follow-ups</span>
                      </li>
                    </ul>

                    <Button
                      onClick={() => navigate("/login?register=true&redirect=homework")}
                      variant={pkg.popular || pkg.id === "free" ? "default" : "outline"}
                      className={`w-full ${colors.btn}`}
                      data-testid={`eduassist-select-${pkg.id}`}
                    >
                      {pkg.id === "free" ? "Start Free Trial" : "Get Started"}
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8">
            All prices are exclusive of 18% GST. Credits are valid for 30 days from
            purchase.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-10 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-purple-500 via-violet-600 to-indigo-700">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-3">
            Ready to Ace Your Homework?
          </h2>
          <p className="text-purple-100 mb-6">
            Join thousands of students who are learning smarter with EduAssist.
          </p>
          <Button
            onClick={() => navigate("/login?register=true&redirect=homework")}
            size="lg"
            className="bg-white text-violet-700 hover:bg-gray-100 px-8 py-6 text-lg rounded-full font-semibold shadow-xl"
            data-testid="eduassist-bottom-cta"
          >
            Start Your Free Trial
            <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>
      </section>

      <SharedFooter />
    </div>
  );
}

